import * as XLSX from "xlsx";
import { EXPECTED_EXPENSE_COLUMNS } from "../constants";
import { normalizeSalesImportBranchId } from "../../salesDataImport/config/salesImportBranchIds";
import { tranDateFromExcelCell } from "../../salesDataImport/services/excelParserService";

function normHeader(h) {
  return String(h ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "");
}

const FIELD_KEYS = {
  branchid: "branchId",
  vouchertypename: "voucherTypeName",
  vouchertype: "voucherTypeName",
  voucherno: "voucherNo",
  vouchern: "voucherNo",
  trandate: "tranDate",
  transdate: "tranDate",
  partyname: "partyName",
  transactiontype: "transactionType",
  transactiontyp: "transactionType",
  amount: "amount",
};

function resolveFieldKey(label) {
  const n = normHeader(label);
  return FIELD_KEYS[n] || null;
}

function cellLabel(cell) {
  if (!cell) return "";
  if (cell.w != null && String(cell.w).trim() !== "") return String(cell.w).trim();
  if (cell.v == null) return "";
  if (cell.v instanceof Date) return "";
  return String(cell.v).trim();
}

function parseNumber(v) {
  if (v == null || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  let s = String(v).trim();
  const neg = /^\(.*\)$/.test(s);
  if (neg) s = s.slice(1, -1);
  s = s.replace(/[₹$,\s]/g, "");
  if (s === "" || s === "-") return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return neg ? -Math.abs(n) : n;
}

function cellToString(v) {
  if (v == null) return "";
  return String(v).trim();
}

function findHeaderRowIndex(sheet, range) {
  const maxScan = Math.min(range.s.r + 8, range.e.r);
  let bestRow = range.s.r;
  let bestHits = 0;
  for (let r = range.s.r; r <= maxScan; r += 1) {
    let hits = 0;
    for (let c = range.s.c; c <= range.e.c; c += 1) {
      const cell = sheet[XLSX.utils.encode_cell({ r, c })];
      if (resolveFieldKey(cellLabel(cell))) hits += 1;
    }
    if (hits > bestHits) {
      bestHits = hits;
      bestRow = r;
    }
  }
  return bestRow;
}

function parseSheetRows(sheet) {
  const range = XLSX.utils.decode_range(sheet["!ref"] || "A1");
  const headerRow = findHeaderRowIndex(sheet, range);
  const colField = {};

  for (let c = range.s.c; c <= range.e.c; c += 1) {
    const cell = sheet[XLSX.utils.encode_cell({ r: headerRow, c })];
    const fk = resolveFieldKey(cellLabel(cell));
    if (fk) colField[c] = fk;
  }

  if (!Object.keys(colField).length) {
    throw new Error(
      `Could not find expense headers. Expected columns like: ${EXPECTED_EXPENSE_COLUMNS.join(", ")}`
    );
  }

  const rows = [];
  for (let r = headerRow + 1; r <= range.e.r; r += 1) {
    const row = { _rowIndex: r + 1 };
    let hasData = false;

    for (let c = range.s.c; c <= range.e.c; c += 1) {
      const fieldKey = colField[c];
      if (!fieldKey) continue;

      const cell = sheet[XLSX.utils.encode_cell({ r, c })];
      if (!cell || (cell.v == null && (cell.w == null || cell.w === ""))) continue;
      hasData = true;

      if (fieldKey === "tranDate") {
        row.tranDate = tranDateFromExcelCell(cell);
      } else if (fieldKey === "branchId") {
        row.branchId = normalizeSalesImportBranchId(cell.v ?? cell.w);
      } else if (fieldKey === "amount") {
        row.amount = parseNumber(cell.v ?? cell.w);
      } else {
        row[fieldKey] = cellToString(cell.v ?? cell.w);
      }
    }

    if (!hasData) continue;
    const errors = validateExpenseRow(row);
    rows.push({ ...row, valid: errors.length === 0, errors });
  }

  return rows;
}

export function validateExpenseRow(row) {
  const errors = [];
  if (!String(row.voucherNo || "").trim()) errors.push("Voucher No is required");
  if (!String(row.tranDate || "").trim()) errors.push("Tran Date is required");
  if (!(Number(row.amount) > 0 || Number(row.amount) < 0)) errors.push("Amount is required");
  return errors;
}

/**
 * @param {ArrayBuffer} buffer
 */
export function parseExpenseExcelBuffer(buffer) {
  const wb = XLSX.read(buffer, { type: "array", cellDates: false });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) throw new Error("Workbook has no sheets.");
  const sheet = wb.Sheets[sheetName];
  const rows = parseSheetRows(sheet);
  if (!rows.length) throw new Error("No data rows found in the file.");
  return { rows, sheetName };
}

/** @param {File} file */
export async function parseExpenseExcelFile(file) {
  const buffer = await file.arrayBuffer();
  return parseExpenseExcelBuffer(buffer);
}

export function rowTranDateInMonth(row, year, monthIndex) {
  const ymd = String(row.tranDate || "").trim();
  const m = ymd.match(/^(\d{4})-(\d{2})-/);
  if (!m) return false;
  return Number(m[1]) === Number(year) && Number(m[2]) === monthIndex + 1;
}
